import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList as TabsListBase, TabsTrigger as TabsTriggerBase, TabsContent as TabsContentBase } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useAuth, hasPermission } from '@/lib/auth';
import { toast } from 'sonner';
import { Plus, KeyRound, Ban, Trash2, Pencil, ShieldCheck, Building2, Users as UsersIcon, Briefcase } from 'lucide-react';

// The Tabs primitives are implemented in .jsx so their props are inferred as
// `any`. Cast them here to keep the rest of the file strictly typed —
// same pattern used elsewhere (RatingTables, BrokerDetail, ClientDetail).
const TabsList = TabsListBase as unknown as React.FC<{ className?: string; children?: React.ReactNode }>;
const TabsTrigger = TabsTriggerBase as unknown as React.FC<{ value: string; className?: string; children?: React.ReactNode; 'data-testid'?: string }>;
const TabsContent = TabsContentBase as unknown as React.FC<{ value: string; className?: string; children?: React.ReactNode }>;

// ------------------------------------------------------------------
// Types
// ------------------------------------------------------------------
interface UserRow {
  id: string;
  email: string;
  full_name: string;
  phone?: string | null;
  role_id: string;
  active: boolean;
  context: string;
  broker_id?: string | null;
  client_id?: string | null;
  last_login_at?: string | null;
  twofa_enabled?: boolean;
  must_reset_password?: boolean;
}
interface Role { id: string; name: string; key?: string }
interface BrokerLite { id: string; name: string }
interface ClientLite { id: string; name: string; trading_name?: string }
interface ClientPortalUser { id: string; full_name?: string; phone?: string | null; email?: string | null; client_id?: string | null; client_name: string; registered: boolean; active: boolean }

type TabKey = 'super_admin' | 'insurer' | 'brokers' | 'clients';

// Map a role.key to one of the 4 pillar tabs so the UI stays stable even
// if new roles get added later. Anything that doesn't match falls back to
// the "Insurer" pillar (safest default for administrative accounts).
const roleKeyToTab = (key: string | undefined): TabKey => {
  const k = (key || '').toLowerCase();
  if (k === 'super_admin' || k === 'master_admin') return 'super_admin';
  if (k === 'broker_admin' || k === 'broker_staff') return 'brokers';
  if (k === 'client_user' || k === 'site_contact') return 'clients';
  return 'insurer';
};

const contextForRole = (roleKey: string | undefined): 'insurer' | 'broker' | 'client' => {
  const k = (roleKey || '').toLowerCase();
  if (k === 'broker_admin' || k === 'broker_staff') return 'broker';
  if (k === 'client_user' || k === 'site_contact') return 'client';
  return 'insurer';
};

// ------------------------------------------------------------------
// Component
// ------------------------------------------------------------------
export const UsersSettings: React.FC = () => {
  const { user: authUser, role } = useAuth();
  const nav = useNavigate();
  const canDelete = hasPermission(role, 'settings_users', 'delete');
  const canUpdate = hasPermission(role, 'settings_users', 'update');

  const [users, setUsers] = useState<UserRow[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [brokers, setBrokers] = useState<BrokerLite[]>([]);
  const [clients, setClients] = useState<ClientLite[]>([]);
  const [portalUsers, setPortalUsers] = useState<ClientPortalUser[]>([]);
  const [activeTab, setActiveTab] = useState<TabKey>('super_admin');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: '', full_name: '', phone: '', role_id: '', context: 'insurer', broker_id: '', client_id: '', force_reset: true, twofa_enabled: true });
  const [lastTemp, setLastTemp] = useState<string | null>(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState<{ linked: Array<{ email?: string; full_name?: string; client_name?: string }>; unmatched: Array<{ email?: string; full_name?: string; candidate_count: number }> } | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [editForm, setEditForm] = useState<Partial<UserRow>>({});
  const [savingEdit, setSavingEdit] = useState(false);

  // ---------- Loaders ----------
  const load = async (): Promise<void> => {
    const [u, r] = await Promise.all([
      api.get<UserRow[]>('/users'),
      api.get<Role[]>('/roles'),
    ]);
    setUsers(u.data);
    setRoles(r.data);
    if (!form.role_id && r.data.length) setForm(f => ({ ...f, role_id: r.data[0].id }));
    // Best-effort — brokers/clients dropdowns for the edit modal.
    try {
      const [b, c] = await Promise.all([
        api.get<BrokerLite[]>('/brokers'),
        api.get<ClientLite[]>('/clients'),
      ]);
      setBrokers(b.data || []);
      setClients(c.data || []);
    } catch (err) {
      console.warn('users page: brokers/clients preload failed', err);
    }
    try {
      const p = await api.get<ClientPortalUser[]>('/users/client-portal');
      setPortalUsers(p.data || []);
    } catch (err) {
      console.warn('users page: client-portal users load failed', err);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, []);

  // ---------- Bucket users into the 4 pillars ----------
  const bucketed = useMemo(() => {
    const out: Record<TabKey, UserRow[]> = { super_admin: [], insurer: [], brokers: [], clients: [] };
    users.forEach((u) => {
      const roleObj = roles.find(r => r.id === u.role_id);
      out[roleKeyToTab(roleObj?.key)].push(u);
    });
    return out;
  }, [users, roles]);

  // Role options filtered to whatever makes sense for the active tab. Prevents
  // e.g. accidentally saving a client as a broker_admin.
  const rolesForTab = (tab: TabKey): Role[] => roles.filter((r) => roleKeyToTab(r.key) === tab);

  // ---------- Mutations ----------
  const create = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      const roleObj = roles.find(r => r.id === form.role_id);
      const ctx = contextForRole(roleObj?.key);
      if (ctx === 'client' && !form.client_id) { toast.error('Select a client for this user'); return; }
      if (ctx === 'broker' && !form.broker_id) { toast.error('Select a broker for this user'); return; }
      const payload = {
        ...form, context: ctx,
        broker_id: ctx === 'broker' ? form.broker_id : null,
        client_id: ctx === 'client' ? form.client_id : null,
      };
      const r = await api.post<{ temp_password: string }>('/users', payload);
      setLastTemp(r.data.temp_password);
      toast.success('User created · temp password copied below');
      setCreating(false);
      setForm({ ...form, email: '', full_name: '', phone: '', broker_id: '', client_id: '' });
      await load();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed to create user');
    }
  };

  const runBackfill = async (): Promise<void> => {
    setBackfilling(true);
    try {
      const r = await api.post<{ scanned: number; linked: NonNullable<typeof backfillResult>['linked']; unmatched: NonNullable<typeof backfillResult>['unmatched'] }>('/users/backfill-client-links', {});
      setBackfillResult({ linked: r.data.linked, unmatched: r.data.unmatched });
      toast.success(`Linked ${r.data.linked.length} user(s) · ${r.data.unmatched.length} need review`);
      await load();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };


  const reset = async (id: string): Promise<void> => {
    try {
      const r = await api.post<{ temp_password: string }>(`/users/${id}/reset-password`);
      setLastTemp(r.data.temp_password);
      toast.success('Password reset · temp password below');
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed to reset password');
    }
  };

  const toggle = async (u: UserRow): Promise<void> => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      await load();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed to update status');
    }
  };

  const del = async (u: UserRow): Promise<void> => {
    if (!window.confirm(
      `Delete ${u.full_name || u.email}?\n\n` +
      `This permanently removes the user, all active sessions, and any broker/client whitelist entries tied to ${u.email}. Audit history is preserved. This cannot be undone.`,
    )) return;
    setDeleting(u.id);
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      await load();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed to delete user');
    } finally {
      setDeleting(null);
    }
  };

  const openEdit = (u: UserRow): void => {
    setEditing(u);
    setEditForm({
      email: u.email,
      full_name: u.full_name,
      phone: u.phone || '',
      role_id: u.role_id,
      broker_id: u.broker_id || '',
      client_id: u.client_id || '',
      active: u.active,
      twofa_enabled: u.twofa_enabled,
      must_reset_password: u.must_reset_password,
    });
  };

  const saveEdit = async (): Promise<void> => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      const roleObj = roles.find(r => r.id === editForm.role_id);
      const nextCtx = contextForRole(roleObj?.key);
      // Strip empty strings so backend treats them as "unset" (None) rather
      // than trying to save '' as e.g. a broker_id.
      const payload: Record<string, unknown> = {
        full_name: editForm.full_name,
        email: editForm.email,
        phone: editForm.phone || null,
        role_id: editForm.role_id,
        active: editForm.active,
        twofa_enabled: editForm.twofa_enabled,
        must_reset_password: editForm.must_reset_password,
        context: nextCtx,
      };
      if (nextCtx === 'broker') {
        payload.broker_id = editForm.broker_id || null;
      } else if (nextCtx === 'client') {
        payload.client_id = editForm.client_id || null;
      }
      await api.patch(`/users/${editing.id}`, payload);
      toast.success('User updated');
      setEditing(null);
      setEditForm({});
      await load();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed to save user');
    } finally {
      setSavingEdit(false);
    }
  };

  // ---------- Tab metadata ----------
  const tabs: Array<{ key: TabKey; label: string; blurb: string; icon: React.ReactNode }> = [
    { key: 'super_admin', label: 'Super Admin', blurb: 'Platform-level administrators with full estate access.', icon: <ShieldCheck size={14} /> },
    { key: 'insurer', label: 'Insurer', blurb: 'Osprey UMA staff — underwriters, risk officers, auditors.', icon: <Building2 size={14} /> },
    { key: 'brokers', label: 'Brokers', blurb: 'Broker-scoped users who can only see their own book.', icon: <Briefcase size={14} /> },
    { key: 'clients', label: 'Clients', blurb: 'Client-tenant users (site contacts, portal users).', icon: <UsersIcon size={14} /> },
  ];

  // ---------- Render ----------
  return (
    <div className="space-y-4" data-testid="users-settings-page">
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <div>
            <CardTitle>Users</CardTitle>
            <CardDescription>Grouped by which pillar of the platform they belong to.</CardDescription>
          </div>
          <Button variant="gradient" size="sm" onClick={() => setCreating(!creating)} data-testid="users-new-btn">
            <Plus size={14} /> New user
          </Button>
        </CardHeader>
        <CardContent>
          {creating && (
            <form onSubmit={create} className="grid md:grid-cols-2 gap-3 p-3 bg-raptor-neutral/40 rounded-md mb-4" data-testid="users-create-form">
              <div className="space-y-1.5"><Label>Email</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-create-email" /></div>
              <div className="space-y-1.5"><Label>Full name</Label><Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="user-create-name" /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="user-create-phone" /></div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })} data-testid="user-create-role">
                  {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </Select>
              </div>
              {(() => {
                const roleObj = roles.find(r => r.id === form.role_id);
                const ctx = contextForRole(roleObj?.key);
                if (ctx === 'broker') {
                  return (
                    <div className="space-y-1.5">
                      <Label>Broker</Label>
                      <Select value={form.broker_id} onChange={(e) => setForm({ ...form, broker_id: e.target.value })} data-testid="user-create-broker">
                        <option value="">— Choose a broker —</option>
                        {brokers.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </Select>
                    </div>
                  );
                }
                if (ctx === 'client') {
                  return (
                    <div className="space-y-1.5">
                      <Label>Client</Label>
                      <Select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="user-create-client">
                        <option value="">— Choose a client —</option>
                        {clients.map(c => <option key={c.id} value={c.id}>{c.trading_name || c.name}</option>)}
                      </Select>
                    </div>
                  );
                }
                return null;
              })()}
              <div className="md:col-span-2 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
                <Button type="submit" variant="gradient">Create user</Button>
              </div>
            </form>
          )}
          {lastTemp && (
            <div className="mb-4 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm font-tabular" data-testid="temp-password-banner">
              Temp password: <b>{lastTemp}</b>. Share once; forces reset on first login.
              <button className="underline ml-2" onClick={() => { navigator.clipboard.writeText(lastTemp); toast.success('Copied'); }}>copy</button>
              <button className="underline ml-2 text-red-600" onClick={() => setLastTemp(null)}>dismiss</button>
            </div>
          )}

          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabKey)}>
            <TabsList className="grid w-full grid-cols-4">
              {tabs.map(t => (
                <TabsTrigger key={t.key} value={t.key} data-testid={`users-tab-${t.key}`}>
                  <span className="inline-flex items-center gap-1.5">
                    {t.icon}
                    {t.label}
                    <Badge variant="muted" className="ml-1 font-tabular text-[10px]">{t.key === 'clients' ? bucketed.clients.length + portalUsers.length : bucketed[t.key].length}</Badge>
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>

            {tabs.map(t => (
              <TabsContent key={t.key} value={t.key} className="mt-4">
                <div className="text-xs text-raptor-navy/60 font-body mb-2">{t.blurb}</div>
                {t.key === 'clients' && canUpdate && (
                  <div className="mb-3 p-3 rounded-md border border-amber-200 bg-amber-50/60">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="text-xs text-raptor-navy/70 font-body">
                        Fix client users that have no client linked (they can't see any surveys until linked).
                        Auto-links where a confident match exists; the rest are listed for review.
                      </div>
                      <Button variant="outline" size="sm" onClick={runBackfill} disabled={backfilling} data-testid="users-backfill-client-links">
                        {backfilling ? 'Scanning…' : 'Fix missing client links'}
                      </Button>
                    </div>
                    {backfillResult && (
                      <div className="mt-2 text-xs font-body space-y-1" data-testid="users-backfill-result">
                        <div className="text-emerald-700">Linked {backfillResult.linked.length}: {backfillResult.linked.map(l => `${l.full_name || l.email} → ${l.client_name}`).join('; ') || '—'}</div>
                        <div className="text-amber-700">Need review {backfillResult.unmatched.length}: {backfillResult.unmatched.map(u => `${u.full_name || u.email}${u.candidate_count > 1 ? ' (multiple matches)' : ''}`).join('; ') || '—'}</div>
                      </div>
                    )}
                  </div>
                )}
                <UserTable
                  users={bucketed[t.key]}
                  roles={roles}
                  authUserId={authUser?.id}
                  canDelete={canDelete}
                  canUpdate={canUpdate}
                  deletingId={deleting}
                  onReset={reset}
                  onToggle={toggle}
                  onEdit={openEdit}
                  onDelete={del}
                />
                {t.key === 'clients' && (
                  <div className="mt-6" data-testid="client-portal-users">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <h3 className="text-sm font-semibold text-raptor-navy">Client-portal users <span className="text-raptor-navy/50 font-normal">({portalUsers.length})</span></h3>
                    </div>
                    <p className="text-xs text-raptor-navy/60 font-body mb-3">
                      Site contacts &amp; portal users who log in via SMS OTP. These are created and managed under each <b>Client → Users</b> tab — shown here read-only so you can see every client user in one place.
                    </p>
                    {portalUsers.length === 0 ? (
                      <div className="text-sm text-raptor-navy/50 border border-dashed border-border rounded-lg py-6 text-center">No client-portal users yet. Add them from a client's Users tab.</div>
                    ) : (
                      <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
                        {portalUsers.map(p => (
                          <div key={p.id} className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-raptor-neutral/30" data-testid={`portal-user-${p.id}`}>
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-raptor-navy truncate">{p.full_name || '—'} <span className="text-raptor-navy/40 font-normal">· {p.phone || 'no phone'}</span></div>
                              <div className="text-xs text-raptor-navy/60 truncate">{p.client_name} · {p.registered ? 'Registered' : 'Pending registration'}</div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Badge variant={p.active ? 'muted' : 'destructive'} className="text-[10px]">{p.active ? 'Active' : 'Revoked'}</Badge>
                              {p.client_id && (
                                <Button variant="outline" size="sm" data-testid={`portal-user-manage-${p.id}`} onClick={() => nav(`/insurer/clients/${p.client_id}?tab=users`)}>Manage</Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* -------- Edit user modal -------- */}
      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) { setEditing(null); setEditForm({}); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
            <DialogDescription>
              Update profile details, change role or move the user to a different pillar.
              Email must remain unique across the tenant.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2" data-testid="user-edit-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Full name</Label>
                <Input value={editForm.full_name || ''} onChange={(e) => setEditForm(f => ({ ...f, full_name: e.target.value }))} data-testid="user-edit-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={editForm.email || ''} onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))} data-testid="user-edit-email" />
              </div>
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input value={editForm.phone || ''} onChange={(e) => setEditForm(f => ({ ...f, phone: e.target.value }))} data-testid="user-edit-phone" />
              </div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={editForm.role_id || ''} onChange={(e) => setEditForm(f => ({ ...f, role_id: e.target.value }))} data-testid="user-edit-role">
                  {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </Select>
              </div>
            </div>

            {(() => {
              const roleObj = roles.find(r => r.id === editForm.role_id);
              const ctx = contextForRole(roleObj?.key);
              if (ctx === 'broker') {
                return (
                  <div className="space-y-1.5">
                    <Label>Broker</Label>
                    <Select value={editForm.broker_id || ''} onChange={(e) => setEditForm(f => ({ ...f, broker_id: e.target.value }))} data-testid="user-edit-broker">
                      <option value="">— Choose a broker —</option>
                      {brokers.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </Select>
                  </div>
                );
              }
              if (ctx === 'client') {
                return (
                  <div className="space-y-1.5">
                    <Label>Client</Label>
                    <Select value={editForm.client_id || ''} onChange={(e) => setEditForm(f => ({ ...f, client_id: e.target.value }))} data-testid="user-edit-client">
                      <option value="">— Choose a client —</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.trading_name || c.name}</option>)}
                    </Select>
                  </div>
                );
              }
              return null;
            })()}

            <div className="grid grid-cols-3 gap-2 pt-1">
              <label className="flex items-center gap-2 text-sm font-body">
                <input
                  type="checkbox"
                  checked={editForm.active ?? true}
                  onChange={(e) => setEditForm(f => ({ ...f, active: e.target.checked }))}
                  data-testid="user-edit-active"
                />
                Active
              </label>
              <label className="flex items-center gap-2 text-sm font-body">
                <input
                  type="checkbox"
                  checked={editForm.twofa_enabled ?? false}
                  onChange={(e) => setEditForm(f => ({ ...f, twofa_enabled: e.target.checked }))}
                  data-testid="user-edit-twofa"
                />
                2FA enabled
              </label>
              <label className="flex items-center gap-2 text-sm font-body">
                <input
                  type="checkbox"
                  checked={editForm.must_reset_password ?? false}
                  onChange={(e) => setEditForm(f => ({ ...f, must_reset_password: e.target.checked }))}
                  data-testid="user-edit-force-reset"
                />
                Force password reset
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditing(null); setEditForm({}); }} disabled={savingEdit} data-testid="user-edit-cancel">Cancel</Button>
            <Button variant="gradient" onClick={() => void saveEdit()} disabled={savingEdit} data-testid="user-edit-save">
              {savingEdit ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ------------------------------------------------------------------
// Table subcomponent
// ------------------------------------------------------------------
interface UserTableProps {
  users: UserRow[];
  roles: Role[];
  authUserId?: string;
  canDelete: boolean;
  canUpdate: boolean;
  deletingId: string | null;
  onReset: (id: string) => void | Promise<void>;
  onToggle: (u: UserRow) => void | Promise<void>;
  onEdit: (u: UserRow) => void;
  onDelete: (u: UserRow) => void | Promise<void>;
}
const UserTable: React.FC<UserTableProps> = ({ users, roles, authUserId, canDelete, canUpdate, deletingId, onReset, onToggle, onEdit, onDelete }) => {
  if (users.length === 0) {
    return (
      <div className="text-center py-6 text-raptor-navy/50 text-sm font-body border border-dashed border-border/60 rounded-md" data-testid="users-empty">
        No users in this pillar yet.
      </div>
    );
  }
  return (
    <table className="w-full text-sm font-body">
      <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
        <tr>
          <th className="py-2 px-3 text-left">ID</th>
          <th className="py-2 px-3 text-left">Name</th>
          <th className="py-2 px-3 text-left">Email</th>
          <th className="py-2 px-3 text-left">Role</th>
          <th className="py-2 px-3 text-left">Status</th>
          <th className="py-2 px-3 text-right">Actions</th>
        </tr>
      </thead>
      <tbody>
        {users.map(u => {
          const roleObj = roles.find(r => r.id === u.role_id);
          const isSelf = u.id === authUserId;
          return (
            <tr key={u.id} className="border-t border-border/60" data-testid={`user-row-${u.id}`}>
              <td className="py-2 px-3 font-tabular text-raptor-navy/70">{u.id}</td>
              <td className="py-2 px-3 font-semibold text-raptor-navy">
                {u.full_name}
                {isSelf && <Badge variant="muted" className="ml-2 text-[10px]">you</Badge>}
              </td>
              <td className="py-2 px-3 font-tabular">{u.email}</td>
              <td className="py-2 px-3">{roleObj?.name ?? u.role_id}</td>
              <td className="py-2 px-3">
                {u.active
                  ? <Badge variant="success">Active</Badge>
                  : <Badge variant="destructive">Inactive</Badge>}
              </td>
              <td className="py-2 px-3">
                <div className="flex gap-1 justify-end">
                  {canUpdate && (
                    <Button size="sm" variant="outline" onClick={() => onEdit(u)} data-testid={`user-edit-${u.id}`}>
                      <Pencil size={12} /> Edit
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => void onReset(u.id)} data-testid={`user-reset-${u.id}`}>
                    <KeyRound size={12} /> Reset
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => void onToggle(u)} data-testid={`user-toggle-${u.id}`}>
                    <Ban size={12} /> {u.active ? 'Deactivate' : 'Activate'}
                  </Button>
                  {canDelete && !isSelf && (
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={deletingId === u.id}
                      onClick={() => void onDelete(u)}
                      data-testid={`user-delete-${u.id}`}
                    >
                      <Trash2 size={12} /> {deletingId === u.id ? 'Deleting…' : 'Delete'}
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default UsersSettings;
